import React, { useContext } from 'react';
import { SupraAccessBenefitSVG } from '@bb/elements-web';
import {
  SupraSubscriptionProductBillingModel,
  SupraSystemSubscriptionStatus,
  SupraSystemSubscriptionType,
} from '@bb/domain/dist/types';
import { camelCase, startCase } from 'lodash';
import { AuthContext } from 'contexts';
import SubscriptionCard from './SubscriptionCard';

export interface SupraOneCardProps {
  /** Whether there was an error fetching who pays for the Supra One subscription. */
  fetchDataError?: boolean;
  /** How the subscription is billed - affects text next to status pill */
  supraSubscriptionPayer?: SupraSubscriptionProductBillingModel;
  /** Callback when button is clicked */
  onButtonClick?: () => void;
}

interface StatusConfig {
  planName: string;
  description: string;
  statusText: string;
  isUpgradeAvailable: boolean;
  /** Whether to treat as subscribed for child card styling */
  isSubscribed: boolean;
  isButtonDisabled: boolean;
}

/** Statuses that represent an existing subscription relationship (any state except fully unsubscribed) */
const ACTIVE_SUPRA_STATUSES: readonly SupraSystemSubscriptionStatus[] = [
  SupraSystemSubscriptionStatus.subscribed,
  SupraSystemSubscriptionStatus.pending,
  SupraSystemSubscriptionStatus.suspended,
  SupraSystemSubscriptionStatus.deactivated,
] as const;

/**
 * Hook that reads the user's Supra One subscription status from auth context.
 * Exported so parent sections can determine whether to render this card
 * without needing the status drilled down as a prop.
 */
export const useSupraSubscriptionStatus = (): SupraSystemSubscriptionStatus => {
  const { auth } = useContext(AuthContext);
  return (
    auth.supraSubscription?.status ?? SupraSystemSubscriptionStatus.unsubscribed
  );
};

const DEFAULT_DESCRIPTION =
  'Access properties and manage your Supra lockboxes directly from your mobile device.';

const UNSUBSCRIBED_DESCRIPTION =
  'Access properties and manage your lockboxes directly from your device.\nReach out to your organization to subscribe.';

const getStatusConfig = (
  status: SupraSystemSubscriptionStatus,
  supraSubscriptionType?: SupraSystemSubscriptionType,
): StatusConfig => {
  const subscribedPlanName = `Supra Access - ${startCase(camelCase(supraSubscriptionType))} Key`;

  // eslint-disable-next-line default-case
  switch (status) {
    case SupraSystemSubscriptionStatus.subscribed:
      return {
        planName: subscribedPlanName,
        description: DEFAULT_DESCRIPTION,
        statusText: 'Active',
        isUpgradeAvailable: false,
        isSubscribed: true,
        isButtonDisabled: false,
      };
    case SupraSystemSubscriptionStatus.pending:
      return {
        planName: 'Supra Access',
        description: DEFAULT_DESCRIPTION,
        statusText: 'Pending',
        isUpgradeAvailable: false,
        isSubscribed: true,
        isButtonDisabled: true,
      };
    case SupraSystemSubscriptionStatus.suspended:
      return {
        planName: subscribedPlanName,
        description: DEFAULT_DESCRIPTION,
        statusText: 'Suspended',
        isUpgradeAvailable: false,
        isSubscribed: true,
        isButtonDisabled: true,
      };
    case SupraSystemSubscriptionStatus.deactivated:
      return {
        planName: 'Supra Access',
        description: UNSUBSCRIBED_DESCRIPTION,
        statusText: 'Deactivated',
        isUpgradeAvailable: true,
        isSubscribed: false,
        isButtonDisabled: true,
      };
    case SupraSystemSubscriptionStatus.unsubscribed:
      return {
        planName: 'Supra Access',
        description: UNSUBSCRIBED_DESCRIPTION,
        statusText: '',
        isUpgradeAvailable: true,
        isSubscribed: false,
        isButtonDisabled: false,
      };
  }
};

/** Resolves the text shown next to the status pill */
const getManagedByText = (
  status: SupraSystemSubscriptionStatus,
  payer?: SupraSubscriptionProductBillingModel,
): string => {
  if (status === SupraSystemSubscriptionStatus.pending) {
    return 'Check email for next steps';
  }

  if (!payer) {
    return '';
  }

  const billingTypeLabels: Record<
    SupraSubscriptionProductBillingModel,
    string
  > = {
    Board: 'Managed by Organization',
    User: '',
  };

  return billingTypeLabels[payer];
};

/**
 * The card displaying information about the user's
 * Supra One subscription. (Or lack thereof.)
 */
const SupraOneCard: React.FC<SupraOneCardProps> = ({
  fetchDataError,
  supraSubscriptionPayer,
  onButtonClick,
}) => {
  const { auth } = useContext(AuthContext);
  const status = useSupraSubscriptionStatus();

  // Grab the subscription type if the user has an active record.
  const hasSupraSubscriptionRecord = ACTIVE_SUPRA_STATUSES.includes(status);
  const supraSubscriptionType = hasSupraSubscriptionRecord
    ? auth.supraSubscription.type
    : null;

  const handleClick = () => {
    onButtonClick?.();
  };

  const config = getStatusConfig(status, supraSubscriptionType);
  const managedByText = getManagedByText(status, supraSubscriptionPayer);

  return (
    <SubscriptionCard
      fetchDataError={fetchDataError}
      isSubscribed={config.isSubscribed}
      allowedToSelfUpgrade={false}
      statusText={config.statusText}
      image={<SupraAccessBenefitSVG />}
      isUpgradeAvailable={config.isUpgradeAvailable}
      isButtonDisabled={config.isButtonDisabled}
      managedByText={managedByText}
      upgradeText="Subscribe by contacting your organization"
      planName={config.planName}
      description={config.description}
      onButtonClick={handleClick}
    />
  );
};

export default SupraOneCard;
